// Person resolution for the task surfaces — the ONE place a personId becomes a name + a face.
//
// Every task DTO carries people as bare `personId`s (assignees, comment authors, activity actors,
// the initiator), so a surface that renders them needs an identity source. That source is the band's
// seat list: `GET /team` widened from `band:manage` to `band:read` with the TM ruling (2026-08-20),
// so EVERY seat that reaches the band can read names + avatar ids — which is why the drawer no longer
// has to fall back to "Teammate" for anyone but a genuine stranger.
//
// A stranger is real and not an error: a task's initiator can be an ORG staffer acting through the
// roster lens, who holds no band seat and therefore appears in no seat list. Each call site passes the
// word it wants for that case ("Someone" reads right in a sentence, "Assignee" in a list), rather than
// this hook guessing one label for every context.
//
// SECOND SOURCE, and it is what made the seat list sufficient again (ART-118). Since
// `core.assignable_people` (808) an ORG seat can be handed a task on a band it holds no seat on — which is
// the whole point of an audience — so the person an assignment names is one the seat list can NEVER
// resolve. The pick succeeded and then rendered as the fallback word over blank initials: "assigning people
// to task not showing the person". The task reads now carry a `people` directory naming every id in the
// payload, resolved server-side from `identity.persons` (no RLS, so no definer). Pass it in. The seat list
// still WINS, so no band member's row changed; the directory only fills what a seat list structurally
// cannot answer.
import { useMemo } from "react";
import type { components } from "@cntrl/api-client";
import { useBandTeam } from "../../app/hooks/useBandTeam.js";
import { useMe } from "../../app/hooks/useSession.js";

export type TaskPersonRef = components["schemas"]["TaskPersonRef"];

export interface TaskPerson {
  readonly personId: string;
  readonly name: string;
  readonly initials: string;
  readonly avatarMediaId: string | null;
  readonly isSelf: boolean;
}

export interface TaskPeople {
  /** The full identity for a personId, or null when no seat matches (an org staffer / a departed member). */
  person: (personId: string | null | undefined) => TaskPerson | null;
  /** A display name, falling back to the caller's own word for an unresolvable person. "You" for self. */
  nameOf: (personId: string | null | undefined, fallback: string) => string;
  /** Initials for an avatar; the fallback's initials when the person is unresolvable. */
  initialsOf: (personId: string | null | undefined, fallback: string) => string;
  /** True once the seat list has resolved — a caller can hold a face back rather than flash initials. */
  isReady: boolean;
}

/** Two-letter initials from a display name ("Casey Rivera" → "CR"); "?" when there's nothing to take. */
export function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";
}

/**
 * Resolve task people off the band's seat list, then off the payload's own directory. Shared by the drawer
 * (assignees, comment authors, activity actors) and the To-do card (the assigner→assignee faces), so the
 * two can never disagree about who someone is.
 *
 * @param directory the `people` block off the task read this surface is rendering. Optional so a caller
 * with no payload in hand still gets seat resolution; pass it wherever you have it, or an org person on the
 * task renders as the fallback word.
 */
export function useTaskPeople(directory?: readonly TaskPersonRef[]): TaskPeople {
  const team = useBandTeam();
  const me = useMe();
  const myPersonId = me.data?.person?.personId;

  return useMemo(
    () => build(directory, team.data?.items ?? [], myPersonId, !team.isLoading),
    [directory, team.data, team.isLoading, myPersonId],
  );
}

/**
 * The DIRECTORY-ONLY resolver — the same people, with no band seat list behind it.
 *
 * CONTEXT-SAFE, and that is the whole reason it is separate rather than a flag on the hook above:
 * `useTaskPeople` runs on `useBandTeam`, which uses the THROWING band context, so it cannot be called from
 * anything that also renders outside a band. The roster's cross-roster surfaces are exactly that — the org
 * home has no `BandProvider` at all, and a to-do row there can belong to any artist on the roster, so there
 * is no ONE seat list that could resolve its people even if a band were mounted.
 *
 * It loses nothing there. A seat list only ever WINS over the directory for a band member, and the whole
 * point of `IOrgTaskAudience` is that the people on these rows are org colleagues, who hold no band seat and
 * whom a seat list structurally cannot name (ART-118). The server directory is the only source that answers
 * for them, and it answers for band members too.
 */
export function useDirectoryPeople(directory?: readonly TaskPersonRef[]): TaskPeople {
  const me = useMe();
  const myPersonId = me.data?.person?.personId;
  return useMemo(() => build(directory, [], myPersonId, true), [directory, myPersonId]);
}

/** The shared resolution, so the two hooks cannot disagree about who someone is or what they are called. */
function build(
  directory: readonly TaskPersonRef[] | undefined,
  seats: readonly { personId?: string | null; displayName?: string | null; avatarMediaId?: string | null }[],
  myPersonId: string | undefined,
  isReady: boolean,
): TaskPeople {
  const byId = new Map<string, TaskPerson>();
  const put = (personId: string, displayName: string | null | undefined, avatarMediaId: string | null | undefined) => {
    const name = displayName || "Member";
    byId.set(personId, {
      personId,
      name,
      initials: initialsFrom(name),
      avatarMediaId: avatarMediaId ?? null,
      isSelf: personId === myPersonId,
    });
  };

  // Directory FIRST so the seat list overwrites it. A band member is described by both, and the seat's
  // displayName is the one every other band surface shows — the directory is the fallback, not a rival.
  for (const ref of directory ?? []) if (ref.personId) put(ref.personId, ref.displayName, ref.avatarMediaId);
  for (const seat of seats) if (seat.personId) put(seat.personId, seat.displayName, seat.avatarMediaId);

  const person = (personId: string | null | undefined): TaskPerson | null =>
    (personId && byId.get(personId)) || null;

  const nameOf = (personId: string | null | undefined, fallback: string): string => {
    if (personId && personId === myPersonId) return "You";
    return person(personId)?.name ?? fallback;
  };

  return {
    person,
    nameOf,
    initialsOf: (personId, fallback) => person(personId)?.initials ?? initialsFrom(fallback),
    isReady,
  };
}

/**
 * IS THIS ROW MINE? — the ONE authority, ART-122 part 3.
 *
 * <b>It is deliberately a CLIENT rule, and that is not the "displayed rule computed in the client" mistake.</b>
 * The two roster payloads carrying task rows are cached ACROSS viewers: `CacheKeys.RosterArtistDashboard` is
 * keyed on (band, money class, team, task audience), and the audience axis is `org:{orgId}` for a seat
 * manager — so every manager in one org shares an entry. A server-stamped "mine" would serve manager A's
 * flags to manager B. The assignee IDS are viewer-independent and cache correctly; the comparison against
 * the viewer belongs where the viewer is known.
 *
 * Being one exported function is what keeps that from becoming four copies with three opinions, which is
 * exactly what `holdCountdown` cost. Note `assigneePersonIds` is NULL-vs-EMPTY on the wire: null means the
 * surface does not carry assignees at all (the person-bound overdue tile), so it can never be "mine" — every
 * row there is already yours and flagging them all says nothing.
 */
export function isAssignedToMe(
  assigneePersonIds: readonly (string | null)[] | null | undefined,
  myPersonId: string | null | undefined,
): boolean {
  if (!assigneePersonIds || !myPersonId) return false;
  return assigneePersonIds.some((id) => id === myPersonId);
}
